import { useEffect, useRef, useState } from "react";
import { Play, Subtitles, Link2, Check, Presentation, CalendarCheck, RotateCcw } from "lucide-react";
import { API } from "@/lib/api";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";

const LANGS = { fr: "Français", en: "English", de: "Deutsch", ar: "العربية" };
const T = {
  fr: { kicker: "Pathologie numérique · Québec", title: "Découvrez Path-Intelligent en 3 minutes", lead: "La plateforme qui réduit l'attente des patients, sécurise chaque lame et rend votre laboratoire conforme à la Loi 25.", cc: "Sous-titres", copy: "Copier le lien", copied: "Lien copié", deck: "Voir la présentation complète", rdv: "Prendre rendez-vous", endT: "Envie de le voir en action ?", endS: "Réservez une démonstration de 30 minutes pour votre laboratoire.", replay: "Revoir" },
  en: { kicker: "Digital pathology · Quebec", title: "Discover Path-Intelligent in 3 minutes", lead: "The platform that cuts patient waiting times, secures every slide and keeps your lab compliant with Quebec's Law 25.", cc: "Subtitles", copy: "Copy link", copied: "Link copied", deck: "See the full presentation", rdv: "Book a demo", endT: "Want to see it in action?", endS: "Book a 30-minute demo for your laboratory.", replay: "Replay" },
  de: { kicker: "Digitale Pathologie · Québec", title: "Entdecken Sie Path-Intelligent in 3 Minuten", lead: "Die Plattform, die Wartezeiten für Patienten verkürzt, jeden Objektträger absichert und Ihr Labor gesetzeskonform macht.", cc: "Untertitel", copy: "Link kopieren", copied: "Link kopiert", deck: "Vollständige Präsentation ansehen", rdv: "Termin vereinbaren", endT: "Möchten Sie es live sehen?", endS: "Buchen Sie eine 30-minütige Demo für Ihr Labor.", replay: "Erneut ansehen" },
  ar: { kicker: "علم الأمراض الرقمي · كيبيك", title: "اكتشف Path-Intelligent في 3 دقائق", lead: "المنصة التي تقلّص انتظار المرضى، وتؤمّن كل شريحة، وتجعل مختبرك متوافقًا مع القانون 25.", cc: "الترجمة", copy: "نسخ الرابط", copied: "تم نسخ الرابط", deck: "عرض التقديم الكامل", rdv: "حجز موعد", endT: "هل ترغب في رؤيته أثناء العمل؟", endS: "احجز عرضًا توضيحيًا مدته 30 دقيقة لمختبرك.", replay: "إعادة" },
};

function initialLang() {
  const q = new URLSearchParams(window.location.search).get("l");
  if (LANGS[q]) return q;
  const s = localStorage.getItem("lang");
  return LANGS[s] ? s : "fr";
}

const post = (url, body) => fetch(`${API}${url}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then((r) => r.json()).catch(() => ({}));

function visitorId() {
  let v = localStorage.getItem("pi_visitor");
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("pi_visitor", v); }
  return v;
}

function useViewTracking(vid, lang, idRef) {
  useEffect(() => {
    const v = vid.current;
    let id = null, started = false, last = 0;
    const send = () => { if (id && v.duration) post(`/video/vue/${id}/progress`, { pct: Math.round((v.currentTime / v.duration) * 100) }); };
    const onPlay = async () => {
      if (started) return;
      started = true;
      id = (await post("/video/vue", { visitor: visitorId(), lang, source: "lien" })).id;
      idRef.current = id;
    };
    const onTime = () => { if (Date.now() - last > 15000) { last = Date.now(); send(); } };
    v.addEventListener("play", onPlay);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("pause", send);
    v.addEventListener("ended", send);
    return () => { send(); v.removeEventListener("play", onPlay); v.removeEventListener("timeupdate", onTime); v.removeEventListener("pause", send); v.removeEventListener("ended", send); };
  }, [vid, lang, idRef]);
}

const pill = (on) =>
  `rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${on ? "bg-sky-600 border-sky-600 text-white" : "bg-white/5 border-white/20 text-slate-200 hover:border-amber-300"}`;

export default function VideoShare() {
  const [lang, setLang] = useState(initialLang);
  const [cc, setCc] = useState(() => localStorage.getItem("pv_cc") === "1");
  const [copied, setCopied] = useState(false);
  const vid = useRef(null);
  const vueId = useRef(null);
  const [ended, setEnded] = useState(false);
  const [rdv, setRdv] = useState(() => new URLSearchParams(window.location.search).get("rdv") === "1");
  const t = T[lang];
  useViewTracking(vid, lang, vueId);

  useEffect(() => {
    const v = vid.current;
    const apply = () => { for (const tr of v.textTracks) tr.mode = cc ? "showing" : "hidden"; };
    apply();
    v.textTracks.onaddtrack = apply;
    const tr = v.querySelector("track");
    if (tr) tr.onload = apply;
  }, [cc, lang]);

  useEffect(() => { document.title = "Path-Intelligent — " + t.title; }, [t]);

  const switchLang = (l) => {
    setLang(l);
    const u = new URL(window.location.href);
    u.searchParams.set("l", l);
    window.history.replaceState(null, "", u);
    setTimeout(() => vid.current?.play().catch(() => {}), 50);
  };
  const toggleCc = () => { localStorage.setItem("pv_cc", cc ? "0" : "1"); setCc(!cc); };
  const copy = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/api/v/${lang}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div dir={lang === "ar" ? "rtl" : "ltr"} className={`min-h-screen bg-[#0B1F33] text-white ${lang === "ar" ? "[&_*]:!tracking-normal [&_*]:![font-family:inherit]" : ""}`}
      style={lang === "ar" ? { fontFamily: "'Noto Sans Arabic','Segoe UI',Tahoma,Arial,sans-serif" } : undefined} data-testid="video-share-page">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700;800&display=swap" />
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-xl bg-sky-600 flex items-center justify-center"><Play className="h-5 w-5 fill-white" /></div>
          <span className="font-bold text-lg tracking-tight">Path-Intelligent</span>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-sky-300 mb-3">{t.kicker}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-4xl" data-testid="video-share-title">{t.title}</h1>
        <p className="text-sm md:text-lg text-slate-300 mt-4 max-w-3xl">{t.lead}</p>

        <div className="relative mt-8 rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_90px_rgba(0,0,0,.55)] bg-black">
          <video key={lang} ref={vid} controls playsInline preload="metadata" crossOrigin="anonymous" onEnded={() => setEnded(true)} onPlay={() => setEnded(false)}
            poster="/onboarding/frames/1_welcome.jpg" className="w-full aspect-video" data-testid="video-share-player">
            <source src={`/video/path-intelligent-${lang}.mp4${lang === "fr" ? "?v=2" : ""}`} type="video/mp4" />
            <track kind="subtitles" srcLang={lang} label={LANGS[lang]} src={`/video/path-intelligent-${lang}.vtt`} default />
          </video>
          {ended && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-4 bg-[#0B1F33]/90 backdrop-blur-sm px-6 animate-in fade-in duration-500" data-testid="video-end-overlay">
              <div className="text-2xl sm:text-4xl font-extrabold">{t.endT}</div>
              <div className="text-sm md:text-lg text-slate-300 max-w-xl">{t.endS}</div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                <button onClick={() => setRdv(true)} className="flex items-center gap-2 rounded-full bg-amber-400 text-slate-900 font-bold px-6 py-3 hover:bg-amber-300 transition-colors" data-testid="video-end-rdv-btn"><CalendarCheck className="h-5 w-5" />{t.rdv}</button>
                <button onClick={() => { setEnded(false); vid.current.currentTime = 0; vid.current.play().catch(() => {}); }} className="flex items-center gap-2 rounded-full border border-white/30 px-5 py-3 hover:border-sky-400 transition-colors" data-testid="video-end-replay-btn"><RotateCcw className="h-4 w-4" />{t.replay}</button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {Object.entries(LANGS).map(([k, label]) => (
            <button key={k} lang={k} dir={k === "ar" ? "rtl" : undefined} style={k === "ar" ? { fontFamily: "'Noto Sans Arabic',Tahoma,sans-serif", letterSpacing: "normal" } : undefined} className={pill(lang === k)} onClick={() => switchLang(k)} data-testid={`video-share-lang-${k}`}>{label}</button>
          ))}
          <button className={`${pill(cc)} flex items-center gap-2`} onClick={toggleCc} aria-pressed={cc} data-testid="video-share-subtitles-toggle">
            <Subtitles className="h-4 w-4" />{t.cc} : {cc ? "✓" : "—"}
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => setRdv(true)} className="flex items-center gap-2 rounded-full bg-sky-600 text-white font-semibold px-5 py-3 hover:bg-sky-500 transition-colors" data-testid="video-share-rdv-btn">
            <CalendarCheck className="h-4 w-4" />{t.rdv}
          </button>
          <button onClick={copy} className="flex items-center gap-2 rounded-full bg-amber-400 text-slate-900 font-semibold px-5 py-3 hover:bg-amber-300 transition-colors" data-testid="video-share-copy-link">
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}{copied ? t.copied : t.copy}
          </button>
          <a href="/presentation.html" className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 hover:border-sky-400 transition-colors" data-testid="video-share-deck-link">
            <Presentation className="h-4 w-4" />{t.deck}
          </a>
        </div>
      </div>
      <DemoRequestDialog open={rdv} onOpenChange={setRdv} lang={lang} vueId={vueId.current} />
    </div>
  );
}
